import React, { createContext, useEffect, useState } from 'react';
import moment from 'moment';

import { fetchQuote, createActivityQuoteDB } from 'components/API/Model';
import createQuoteTemplate from 'assets/json-templates/CreateQuoteTemplate';
import filterByTemplate from 'assets/json-templates/TemplateFilter';
import { getHO6OccupancyType, getUsageType } from 'tools/CreateQuoteHelper';

export const AppContext = createContext();

const AppContextProvider = ({ children }) => {
	const [displayedQuoteNumber, setDisplayedQuoteNumber] = useState(null);
	const [navigationTitle, setNavigationTitle] = useState(null);

	const setDTOBuildingField = (propertyName, value, ...dtoBuildingItems) => {
		const items = dtoBuildingItems.flat();
		items.forEach((dtoBuilding) => (dtoBuilding[propertyName] = value));
	};

	const setDTOBuildingDefaults = (...dtoBuildingItems) => {
		setDTOBuildingField('OpeningProtection', 'Basic', dtoBuildingItems);
		setDTOBuildingField(
			'HurricaneAttachedAluminum',
			'Yes',
			dtoBuildingItems,
		);
		setDTOBuildingField('RoofWallAttachment', 'Unknown', dtoBuildingItems);
		setDTOBuildingField('GateGuarded', 'None', dtoBuildingItems);
		setDTOBuildingField('FireAlarmCd', 'None', dtoBuildingItems);
		setDTOBuildingField('BurglarAlarmCd', 'None', dtoBuildingItems);
		setDTOBuildingField('SprinklerSystem', 'None', dtoBuildingItems);
		setDTOBuildingField('RoofSlope', 'Unknown', dtoBuildingItems);
		setDTOBuildingField('RoofDeck', 'Unknown', dtoBuildingItems);
		setDTOBuildingField('RoofCoverAgeClass', 'Non-FBC', dtoBuildingItems);
		setDTOBuildingField(
			'SecondaryWaterResistance',
			'Unknown',
			dtoBuildingItems,
		);
		setDTOBuildingField('WaterMonitoringSystem', 'None', dtoBuildingItems);
		setDTOBuildingField(
			'AdditionsAlterationsIncrease',
			'0',
			dtoBuildingItems,
		);
		setDTOBuildingField('CompEquipCov', '0', dtoBuildingItems);
		setDTOBuildingField(
			'EquipServiceEnhancement',
			'None',
			dtoBuildingItems,
		);
		setDTOBuildingField(
			'OptionalSinkholeLoss',
			'Excluded',
			dtoBuildingItems,
		);
		setDTOBuildingField('TheftFraudExpenseCov', 'No', dtoBuildingItems);
		setDTOBuildingField('AllPerilDed', '1000', dtoBuildingItems);
		setDTOBuildingField('WaterDed', '1000', dtoBuildingItems);
		setDTOBuildingField('LimitedFMWDBCov', '10000/50000', dtoBuildingItems);
		setDTOBuildingField('LimitedSECCov', '0', dtoBuildingItems);
		setDTOBuildingField('PersonalInjuryCov', 'No', dtoBuildingItems);
		setDTOBuildingField(
			'IncreasedReplacementCostDwelling',
			'No',
			dtoBuildingItems,
		);
		setDTOBuildingField('SpecialPersonalPropCov', 'No', dtoBuildingItems);
		setDTOBuildingField(
			'PersonalPropertyReplacementCost',
			'No',
			dtoBuildingItems,
		);
		setDTOBuildingField('CovBLimitPct', '0.02', dtoBuildingItems);
		setDTOBuildingField('CovCLimitPct', '0.25', dtoBuildingItems);
		setDTOBuildingField(
			'FeetToHydrantOrWaterSource',
			'0-1,000',
			dtoBuildingItems,
		);
		setDTOBuildingField('HomeStyle', 'Contemporary', dtoBuildingItems);
		setDTOBuildingField(
			'OccupancyType',
			localStorage.getItem('occupancy_type'),
			dtoBuildingItems,
		);
		if (!dtoBuildingItems[0].UsageType)
			setDTOBuildingField(
				'UsageType',
				'Primary Residence, Not Rented',
				dtoBuildingItems,
			);
	};

	const setDTOBasicPolicyDefaultsForSpecificPolicyType = (
		policyType,
		dtoBasicPolicy,
	) => {
		if (
			policyType === 'DF3-DO' ||
			policyType === 'DF1' ||
			policyType === 'DF3-DL' ||
			policyType === 'HO4' ||
			policyType === 'HO6'
		) {
			dtoBasicPolicy.PurchaseDt = '20220101';
		}
	};

	const setDTOBuildingDefaultsForCpecificPolicyType = (
		policyType,
		...dtoBuildingItems
	) => {
		const currentDTOBuilding = dtoBuildingItems[0];

		if (
			currentDTOBuilding['LocationOfTerrain'] === 'Terrain C' ||
			currentDTOBuilding['RoofDeck'] === 'Reinforced Concrete'
		) {
			setDTOBuildingField(
				'OpeningProtection',
				'Hurricane',
				currentDTOBuilding,
			);
		}

		if (policyType === 'DF3-DO') {
			setDTOBuildingField('HurricaneDed', '.02', currentDTOBuilding);
			setDTOBuildingField(
				'CovCLimit',
				Math.round(
					Number(currentDTOBuilding.CovALimit) *
						Number(currentDTOBuilding.CovCLimitPct),
				),
				currentDTOBuilding,
			);
			if (
				currentDTOBuilding['UsageType'] ===
				'Primary Residence, Not Rented'
			) {
				setDTOBuildingField('CovLLimit', '100000', currentDTOBuilding);
			} else {
				setDTOBuildingField('CovPLimit', '100000', currentDTOBuilding);
			}
			setDTOBuildingField('CovMLimit', '1000', currentDTOBuilding);

			if (!currentDTOBuilding['BuildingCodeGrade']) {
				setDTOBuildingField(
					'BuildingCodeGrade',
					'Non-Participating (98)',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['ProtectionClass']) {
				setDTOBuildingField('ProtectionClass', '3', currentDTOBuilding);
			}

			if (!currentDTOBuilding['FeetToHydrantOrWaterSource']) {
				setDTOBuildingField(
					'FeetToHydrantOrWaterSource',
					'0-1,000',
					currentDTOBuilding,
				);
			}

			if (currentDTOBuilding['WindSpeedRegion'] === 'HVHZ') {
				setDTOBuildingField(
					'OpeningProtection',
					'Hurricane',
					currentDTOBuilding,
				);
			}
		}

		if (policyType === 'DF1') {
			setDTOBuildingField('HurricaneDed', '1000', currentDTOBuilding);
			setDTOBuildingField('CovDLimitPct', '0.02', currentDTOBuilding);
			setDTOBuildingField(
				'CovDLimit',
				Math.round(
					Number(currentDTOBuilding.CovALimit) *
						Number(currentDTOBuilding.covDLimitPct),
				),
				currentDTOBuilding,
			);
			setDTOBuildingField(
				'CovCLimit',
				Math.round(
					Number(currentDTOBuilding.CovALimit) *
						Number(currentDTOBuilding.CovCLimitPct),
				),
				currentDTOBuilding,
			);
			setDTOBuildingField(
				'CovBLimit',
				Math.round(
					Number(currentDTOBuilding.CovALimit) *
						Number(currentDTOBuilding.CovBLimitPct),
				),
				currentDTOBuilding,
			);
			if (
				currentDTOBuilding['UsageType'] ===
				'Primary Residence, Not Rented'
			) {
				setDTOBuildingField('CovLLimit', '100000', currentDTOBuilding);
			} else {
				setDTOBuildingField('CovPLimit', '100000', currentDTOBuilding);
			}
			setDTOBuildingField('CovMLimit', '1000', currentDTOBuilding);

			if (!currentDTOBuilding['BuildingCodeGrade']) {
				setDTOBuildingField(
					'BuildingCodeGrade',
					'Non-Participating (98)',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['ProtectionClass']) {
				setDTOBuildingField('ProtectionClass', '3', currentDTOBuilding);
			}

			if (!currentDTOBuilding['FeetToHydrantOrWaterSource']) {
				setDTOBuildingField(
					'FeetToHydrantOrWaterSource',
					'0-1,000',
					currentDTOBuilding,
				);
			}

			if (currentDTOBuilding['WindSpeedRegion'] === 'HVHZ') {
				setDTOBuildingField(
					'OpeningProtection',
					'Hurricane',
					currentDTOBuilding,
				);
			}
		}

		if (policyType === 'DF3-DL') {
			setDTOBuildingField('HurricaneDed', '1000', currentDTOBuilding);
			setDTOBuildingField('CovPLimit', '100000', currentDTOBuilding);
			setDTOBuildingField('CovMLimit', '1000', currentDTOBuilding);
			setDTOBuildingField(
				'CovCLimit',
				Math.round(
					Number(currentDTOBuilding.CovALimit) *
						Number(currentDTOBuilding.CovCLimitPct),
				),
				currentDTOBuilding,
			);
			setDTOBuildingField(
				'UsageType',
				'Primary Residence, Rented',
				currentDTOBuilding,
			);
			setDTOBuildingField('RentalTerm', 'Annual', currentDTOBuilding);

			if (!currentDTOBuilding['BuildingCodeGrade']) {
				setDTOBuildingField(
					'BuildingCodeGrade',
					'Non-Participating (98)',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['ProtectionClass']) {
				setDTOBuildingField('ProtectionClass', '3', currentDTOBuilding);
			}

			if (!currentDTOBuilding['FeetToHydrantOrWaterSource']) {
				setDTOBuildingField(
					'FeetToHydrantOrWaterSource',
					'0-1,000',
					currentDTOBuilding,
				);
			}
		}

		if (policyType === 'HO6' || policyType == 'HO4') {
			setDTOBuildingField('HurricaneDed', '1000', dtoBuildingItems);
			setDTOBuildingField('CovELimit', '100000', currentDTOBuilding);
			setDTOBuildingField('CovFLimit', '1000', currentDTOBuilding);
			if (policyType === 'HO6') {
				setDTOBuildingField(
					'CovCLimit',
					Math.max(20000, Math.round(
						Number(currentDTOBuilding.CovALimit) *
						Number(currentDTOBuilding.CovCLimitPct),
					)),
					currentDTOBuilding,
				);
			}
			setDTOBuildingField('RentalTerm', 'Annual', currentDTOBuilding);

			if (+currentDTOBuilding['YearRoofBuilt'] > 2001) {
				setDTOBuildingField(
					'RoofCoverAgeClass',
					'FBC',
					currentDTOBuilding,
				);
			} else {
				setDTOBuildingField(
					'RoofCoverAgeClass',
					'Non-FBC',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['LocationOfTerrain']) {
				setDTOBuildingField(
					'LocationOfTerrain',
					'Terrain B',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['WindSpeedDesign']) {
				setDTOBuildingField(
					'WindSpeedDesign',
					'100 mph or greater',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['DesignExposure']) {
				setDTOBuildingField(
					'DesignExposure',
					'Standard',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['WindSpeedLocation']) {
				setDTOBuildingField(
					'WindSpeedLocation',
					'100 mph',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['BuildingCodeGrade']) {
				setDTOBuildingField(
					'BuildingCodeGrade',
					'Non-Participating (98)',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['ProtectionClass']) {
				setDTOBuildingField('ProtectionClass', '3', currentDTOBuilding);
			}

			if (!currentDTOBuilding['FeetToHydrantOrWaterSource']) {
				setDTOBuildingField(
					'FeetToHydrantOrWaterSource',
					'0-1,000',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['NumberOfStoriesUnit']) {
				setDTOBuildingField(
					'NumberOfStoriesUnit',
					'1',
					currentDTOBuilding,
				);
			}

			if (policyType === 'HO4') {
				setDTOBuildingField(
					'OccupancyType',
					'Tenant Occupied',
					currentDTOBuilding,
				);
				setDTOBuildingField('UsageType', 'Rental Only', currentDTOBuilding);
			} else {
				// HO6 — map based on user's selection
				const ot = localStorage.getItem('occupancy_type');
				const re = localStorage.getItem('rental_exposure');
				setDTOBuildingField(
					'OccupancyType',
					getHO6OccupancyType(ot),
					currentDTOBuilding,
				);
				const ut = getUsageType('HO6', ot, re);
				if (ut) {
					setDTOBuildingField('UsageType', ut, currentDTOBuilding);
				}
			}
		}

		if (policyType === 'HO5' || policyType == 'HO3') {
			setDTOBuildingField('HurricaneDed', '.02', dtoBuildingItems);
			setDTOBuildingField('CovELimit', '100000', currentDTOBuilding);
			setDTOBuildingField('CovFLimit', '1000', currentDTOBuilding);

			if (currentDTOBuilding['WindSpeedRegion'] === 'HVHZ') {
				setDTOBuildingField(
					'OpeningProtection',
					'Hurricane',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['BuildingCodeGrade']) {
				setDTOBuildingField(
					'BuildingCodeGrade',
					'Non-Participating (98)',
					currentDTOBuilding,
				);
			}

			if (!currentDTOBuilding['ProtectionClass']) {
				setDTOBuildingField('ProtectionClass', '3', currentDTOBuilding);
			}

			if (!currentDTOBuilding['FeetToHydrantOrWaterSource']) {
				setDTOBuildingField(
					'FeetToHydrantOrWaterSource',
					'0-1,000',
					currentDTOBuilding,
				);
			}
		}

		if (!currentDTOBuilding['OpeningProtection']) {
			setDTOBuildingField(
				'OpeningProtection',
				'Basic',
				currentDTOBuilding,
			);
		}
	};

	const fetchQuoteAndLog = async (page, quoteNumber = null) => {
		createActivityQuoteDB(page, 'API_REQ', 'Fetch quote request');
		const applicationNumber =
			quoteNumber ?? localStorage.getItem('quote_number');
		const fetchQuotePayload = JSON.stringify({
			ApplicationNumber: applicationNumber,
			Owner: 'SalesPortal',
		});
		const response = await fetchQuote(fetchQuotePayload);

		if (response !== 'Error') {
			createActivityQuoteDB(
				page,
				'API_RES',
				'Fetch quote response - SUCCESS',
			);

			if (!localStorage.getItem('product_version')) {
				localStorage.setItem(
					'product_version',
					response.DTOBasicPolicy[0].ProductVersionIdRef,
				);
			}
		}
		return response;
	};

	useEffect(() => {
		if (!displayedQuoteNumber) return;

		const titleText = `Quote number: ${displayedQuoteNumber}`;
		setNavigationTitle(titleText);
	}, [displayedQuoteNumber]);

	return (
		<AppContext.Provider
			value={{
				navigationTitle,
				setDisplayedQuoteNumber,
				setDTOBuildingField,
				setDTOBuildingDefaults,
				setDTOBuildingDefaultsForCpecificPolicyType,
				setDTOBasicPolicyDefaultsForSpecificPolicyType,
				fetchQuoteAndLog,
			}}
		>
			{children}
		</AppContext.Provider>
	);
};

export default AppContextProvider;
